import { ImageLightbox } from '@/components/ui/image-lightbox';
import { cn } from '@/lib/utils';
import { useThumbnailLightbox } from './product-thumbnail-lightbox-context';

export type ProductThumbnailProps = {
  imageUrl?: string | null;
  alt?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  borderStyle?: 'white' | 'none';
  /** Enable lightbox on click (default: false) */
  enableLightbox?: boolean;
  /** Load immediately for above-the-fold / LCP images */
  priority?: boolean;
};

const sizeClasses = {
  sm: 'w-10 h-10',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
  xl: 'w-20 h-20',
  '2xl': 'w-24 h-24',
};

const sizePixels = {
  sm: 40,
  md: 48,
  lg: 64,
  xl: 80,
  '2xl': 96,
} as const;

const borderClasses = {
  white: 'border-2 border-white shadow',
  none: '',
};

type ThumbnailImageProps = {
  imageUrl: string;
  alt: string;
  size: NonNullable<ProductThumbnailProps['size']>;
  className?: string;
  borderStyle: NonNullable<ProductThumbnailProps['borderStyle']>;
  priority: boolean;
  interactive: boolean;
};

function ThumbnailImage({
  imageUrl,
  alt,
  size,
  className,
  borderStyle,
  priority,
  interactive,
}: ThumbnailImageProps) {
  const sizeClass = sizeClasses[size];
  const pixelSize = sizePixels[size];
  const borderClass = borderClasses[borderStyle];

  return (
    <div
      className={cn(
        sizeClass,
        'relative shrink-0 overflow-hidden rounded-md bg-slate-100',
        borderClass,
        className,
      )}
    >
      <img
        src={imageUrl}
        alt={alt}
        width={pixelSize}
        height={pixelSize}
        loading={priority ? 'eager' : 'lazy'}
        {...(priority ? { fetchpriority: 'high' as const } : { fetchpriority: 'low' as const })}
        decoding="async"
        className={cn(
          'h-full w-full object-cover',
          interactive && 'cursor-pointer hover:brightness-95',
        )}
      />
    </div>
  );
}

function ThumbnailPlaceholder({
  size,
  className,
  borderStyle,
}: {
  size: NonNullable<ProductThumbnailProps['size']>;
  className?: string;
  borderStyle: NonNullable<ProductThumbnailProps['borderStyle']>;
}) {
  return (
    <div
      className={cn(
        sizeClasses[size],
        'bg-gradient-to-br from-slate-200 to-slate-300 rounded-md flex items-center justify-center',
        borderClasses[borderStyle],
        className,
      )}
    >
      <span className="text-xl text-slate-400">📦</span>
    </div>
  );
}

/** Static thumbnail — no lightbox context, minimal DOM. Use in dense grids. */
export function ProductThumbnailStatic({
  imageUrl,
  alt = 'Product',
  size = 'md',
  className,
  borderStyle = 'white',
  priority = false,
}: Omit<ProductThumbnailProps, 'enableLightbox'>) {
  if (imageUrl) {
    return (
      <ThumbnailImage
        imageUrl={imageUrl}
        alt={alt}
        size={size}
        className={className}
        borderStyle={borderStyle}
        priority={priority}
        interactive={false}
      />
    );
  }

  return <ThumbnailPlaceholder size={size} className={className} borderStyle={borderStyle} />;
}

function ProductThumbnailLightbox({
  imageUrl,
  alt = 'Product',
  size = 'md',
  className,
  borderStyle = 'white',
  priority = false,
}: Omit<ProductThumbnailProps, 'enableLightbox'>) {
  const sharedLightbox = useThumbnailLightbox();

  if (!imageUrl) {
    return <ThumbnailPlaceholder size={size} className={className} borderStyle={borderStyle} />;
  }

  const imageElement = (
    <ThumbnailImage
      imageUrl={imageUrl}
      alt={alt}
      size={size}
      className={className}
      borderStyle={borderStyle}
      priority={priority}
      interactive
    />
  );

  if (sharedLightbox) {
    return (
      <button
        type="button"
        onClick={() => sharedLightbox.openLightbox({ src: imageUrl, alt, title: alt })}
        className="group relative"
        aria-label={`View ${alt}`}
      >
        {imageElement}
        <span className="absolute inset-0 rounded-md ring-0 ring-blue-300/0 group-hover:ring-2 group-hover:ring-blue-300/40 transition" />
      </button>
    );
  }

  return (
    <ImageLightbox src={imageUrl} alt={alt} title={alt}>
      {({ onClick }) => (
        <button
          type="button"
          onClick={onClick}
          className="group relative"
          aria-label={`View ${alt}`}
        >
          {imageElement}
          <span className="absolute inset-0 rounded-md ring-0 ring-blue-300/0 group-hover:ring-2 group-hover:ring-blue-300/40 transition" />
        </button>
      )}
    </ImageLightbox>
  );
}

export function ProductThumbnail({
  imageUrl,
  alt = 'Product',
  size = 'md',
  className,
  borderStyle = 'white',
  enableLightbox = true,
  priority = false,
}: ProductThumbnailProps) {
  if (!enableLightbox) {
    return (
      <ProductThumbnailStatic
        imageUrl={imageUrl}
        alt={alt}
        size={size}
        className={className}
        borderStyle={borderStyle}
        priority={priority}
      />
    );
  }

  return (
    <ProductThumbnailLightbox
      imageUrl={imageUrl}
      alt={alt}
      size={size}
      className={className}
      borderStyle={borderStyle}
      priority={priority}
    />
  );
}
